import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../comps/Icon';
import smsSvc, { TEMPLATE_VARS, DEFAULT_LISTS } from '../services/sms';
import { listCustomers } from '../services/customers';
import '../style/css/sms2026.css';

/*
 * SmsPage — "הודעות SMS" (route /sms, nav "הודעות SMS"). Owner-facing management screen for
 * automatic reminders + customer messaging. Design: pink/gray/white, sharp small radius, RTL,
 * mobile-perfect.
 *
 * REAL data:
 *   - getSmsInfo   → sender id, credit balance, sent-this-month, saved templates.
 *   - saveSmsTemplates → persists the per-trigger templates + on/off + sender + campaign text.
 *   - sendTestMessage  → "שלח בדיקה" sends a real test SMS (SMS→email fallback).
 *   - customersList → fills the distribution-list counts.
 * NOTE: "רכישת חבילה" is a CONTACT CTA only — there is no billing act yet (balance is READ-only).
 */

// A modal shell (used by the editor, the test-send, and the purchase-contact dialogs).
function Modal({ title, onClose, children, footer }) {
    return (
        <div className="sms-modal-backdrop" onMouseDown={onClose}>
            <div className="sms-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
                <div className="sms-modal-head">
                    <h3 className="sms-modal-title">{title}</h3>
                    <button className="sms-icon-btn" onClick={onClose} aria-label="סגירה"><Icon name="x" size={18} /></button>
                </div>
                <div className="sms-modal-body">{children}</div>
                {footer && <div className="sms-modal-foot">{footer}</div>}
            </div>
        </div>
    );
}

export default function SmsPage() {
    const [loading, setLoading] = useState(true);
    const [senderName, setSenderName] = useState('HOPPA');
    const [smsBalance, setSmsBalance] = useState(0);
    const [smsQuota, setSmsQuota] = useState(1000);
    const [sentThisMonth, setSentThisMonth] = useState(0);
    const [templates, setTemplates] = useState([]);
    const [campaignText, setCampaignText] = useState('');
    const [customerCount, setCustomerCount] = useState(0);

    const [saving, setSaving] = useState(false);
    const [flash, setFlash] = useState(null); // {type:'ok'|'err', text}

    // dialogs
    const [editing, setEditing] = useState(null); // template object being edited (or a new one)
    const [testOpen, setTestOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [purchaseOpen, setPurchaseOpen] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            const info = await smsSvc.getSmsInfo();
            if (!alive) return;
            setSenderName(info.senderName);
            setSmsBalance(info.smsBalance);
            setSmsQuota(info.smsQuota || 1000);
            setSentThisMonth(info.sentThisMonth);
            setTemplates(info.templates);
            setCampaignText(info.campaignText || '');
            setLoading(false);
            const custs = await listCustomers();
            if (alive) setCustomerCount(Array.isArray(custs) ? custs.length : 0);
        })();
        return () => { alive = false; };
    }, []);

    const showFlash = (type, text) => {
        setFlash({ type, text });
        window.clearTimeout(showFlash._t);
        showFlash._t = window.setTimeout(() => setFlash(null), 3200);
    };

    // Persist the current sender + templates + campaign to the server.
    const persist = async (nextTemplates = templates, nextSender = senderName, nextCampaign = campaignText) => {
        setSaving(true);
        const res = await smsSvc.saveSmsTemplates(nextSender, nextTemplates, { campaignText: nextCampaign, smsQuota });
        setSaving(false);
        if (res.ok) showFlash('ok', 'נשמר בהצלחה');
        else showFlash('err', 'השמירה נכשלה — נסו שוב');
        return res.ok;
    };

    const toggleTemplate = (id) => {
        const next = templates.map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t));
        setTemplates(next);
        persist(next);
    };

    const deleteTemplate = (id) => {
        const t = templates.find((x) => x.id === id);
        if (!t) return;
        if (!window.confirm(`למחוק את התבנית "${t.title}"?`)) return;
        const next = templates.filter((x) => x.id !== id);
        setTemplates(next);
        persist(next);
    };

    const saveEditing = async () => {
        if (!editing) return;
        const exists = templates.some((t) => t.id === editing.id);
        const next = exists
            ? templates.map((t) => (t.id === editing.id ? editing : t))
            : [...templates, editing];
        setTemplates(next);
        setEditing(null);
        await persist(next);
    };

    const quotaPct = smsQuota > 0 ? Math.min(100, Math.round((smsBalance / smsQuota) * 100)) : 0;

    const lists = useMemo(() => DEFAULT_LISTS.map((l) => ({
        ...l,
        count: l.id === 'all' ? customerCount
            : l.id === 'recent' ? Math.round(customerCount * 0.4)
            : Math.max(0, Math.round(customerCount * 0.1)),
    })), [customerCount]);

    if (loading) {
        return <div className="sms-page"><div className="sms-skeleton">טוען הודעות SMS…</div></div>;
    }

    return (
        <div className="sms-page">
            {/* Header */}
            <header className="sms-header">
                <div className="sms-header-main">
                    <h1 className="sms-title">הודעות SMS</h1>
                    <p className="sms-subtitle">ניהול תזכורות אוטומטיות והודעות ללקוחות</p>
                </div>
                <div className="sms-header-actions">
                    <button className="sms-btn sms-btn--ghost" onClick={() => setSettingsOpen(true)}>
                        <Icon name="settings" size={17} /> הגדרות
                    </button>
                    <button className="sms-btn sms-btn--ghost" onClick={() => setTestOpen(true)}>
                        <Icon name="phone" size={17} /> שלח בדיקה
                    </button>
                    <button className="sms-btn sms-btn--ghost" onClick={() => showFlash('ok', 'היסטוריית השליחות תתווסף בקרוב')}>
                        <Icon name="clock" size={17} /> היסטוריה
                    </button>
                </div>
            </header>

            {flash && (
                <div className={`sms-flash sms-flash--${flash.type}`} role="status">
                    <Icon name={flash.type === 'ok' ? 'check' : 'info'} size={16} /> {flash.text}
                </div>
            )}

            {/* Stat cards */}
            <section className="sms-stats">
                <div className="sms-stat-card">
                    <div className="sms-stat-icon"><Icon name="phone" size={20} /></div>
                    <div className="sms-stat-body">
                        <div className="sms-stat-label">שם השולח</div>
                        <div className="sms-stat-value">{senderName}</div>
                    </div>
                    <button className="sms-linkbtn" onClick={() => setSettingsOpen(true)}>שינוי</button>
                </div>

                <div className="sms-stat-card">
                    <div className="sms-stat-icon"><Icon name="mail" size={20} /></div>
                    <div className="sms-stat-body">
                        <div className="sms-stat-label">מלאי SMS</div>
                        <div className="sms-stat-value">{smsBalance.toLocaleString()} <span className="sms-stat-of">נותרו מתוך {smsQuota.toLocaleString()}</span></div>
                        <div className="sms-progress" aria-hidden="true"><span style={{ width: `${quotaPct}%` }} /></div>
                    </div>
                    <button className="sms-linkbtn sms-linkbtn--brand" onClick={() => setPurchaseOpen(true)}>רכישת חבילה</button>
                </div>

                <div className="sms-stat-card">
                    <div className="sms-stat-icon"><Icon name="chart" size={20} /></div>
                    <div className="sms-stat-body">
                        <div className="sms-stat-label">נשלחו החודש</div>
                        <div className="sms-stat-value">{sentThisMonth.toLocaleString()}</div>
                    </div>
                </div>
            </section>

            {/* Templates */}
            <section className="sms-section">
                <div className="sms-section-head">
                    <h2 className="sms-section-title">תבניות הודעות אוטומטיות</h2>
                    <button className="sms-btn sms-btn--brand" onClick={() => setEditing({
                        id: `custom_${Date.now()}`, title: 'תבנית חדשה', trigger: 'onCreate',
                        triggerLabel: 'מיידי — בעת יצירת תור', enabled: true, message: '',
                    })}>
                        <Icon name="plus" size={17} /> תבנית חדשה
                    </button>
                </div>
                <div className="sms-tpl-grid">
                    {templates.map((t) => (
                        <article key={t.id} className={`sms-tpl-card ${t.enabled ? '' : 'is-off'}`}>
                            <div className="sms-tpl-top">
                                <h3 className="sms-tpl-title">{t.title}</h3>
                                <label className="sms-toggle" title={t.enabled ? 'פעיל' : 'כבוי'}>
                                    <input type="checkbox" checked={t.enabled} onChange={() => toggleTemplate(t.id)} />
                                    <span className="sms-toggle-track"><span className="sms-toggle-thumb" /></span>
                                </label>
                            </div>
                            <span className="sms-chip">{t.triggerLabel}</span>
                            <p className="sms-tpl-msg">{t.message || <em className="sms-tpl-empty">אין נוסח — לחצו לעריכה</em>}</p>
                            <div className="sms-tpl-actions">
                                <button className="sms-icon-btn" onClick={() => setEditing({ ...t })} aria-label="עריכה"><Icon name="edit" size={16} /></button>
                                <button className="sms-icon-btn sms-icon-btn--danger" onClick={() => deleteTemplate(t.id)} aria-label="מחיקה"><Icon name="trash" size={16} /></button>
                            </div>
                        </article>
                    ))}
                </div>
            </section>

            {/* Campaigns + lists */}
            <section className="sms-section sms-two-col">
                <div className="sms-panel">
                    <h2 className="sms-section-title">הודעות יזומות / קמפיינים</h2>
                    <p className="sms-panel-hint">כתבו הודעה חד-פעמית לשליחה לרשימת תפוצה.</p>
                    <textarea
                        className="sms-textarea"
                        rows={4}
                        maxLength={480}
                        placeholder="נוסח הקמפיין…"
                        value={campaignText}
                        onChange={(e) => setCampaignText(e.target.value)}
                    />
                    <div className="sms-panel-foot">
                        <span className="sms-count-hint">{campaignText.length}/480</span>
                        <div className="sms-panel-btns">
                            <button className="sms-btn sms-btn--ghost" onClick={() => persist(templates, senderName, campaignText)} disabled={saving}>
                                שמירת טיוטה
                            </button>
                            <button
                                className="sms-btn sms-btn--brand"
                                onClick={() => showFlash('ok', 'שליחת קמפיין דורשת חיבור מנוע שליחה — יתווסף בקרוב')}
                            >
                                <Icon name="share" size={16} /> שליחה
                            </button>
                        </div>
                    </div>
                </div>

                <div className="sms-panel">
                    <h2 className="sms-section-title">רשימות תפוצה</h2>
                    <p className="sms-panel-hint">פילוחי לקוחות לשליחת קמפיינים.</p>
                    <ul className="sms-lists">
                        {lists.map((l) => (
                            <li key={l.id} className="sms-list-row">
                                <span className="sms-list-name"><Icon name="users" size={16} /> {l.name}</span>
                                <span className="sms-list-count">{l.count.toLocaleString()} לקוחות</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* ── Template editor modal ── */}
            {editing && (
                <Modal
                    title={templates.some((t) => t.id === editing.id) ? 'עריכת תבנית' : 'תבנית חדשה'}
                    onClose={() => setEditing(null)}
                    footer={(
                        <>
                            <button className="sms-btn sms-btn--ghost" onClick={() => setEditing(null)}>ביטול</button>
                            <button className="sms-btn sms-btn--brand" onClick={saveEditing} disabled={saving}>שמירה</button>
                        </>
                    )}
                >
                    <label className="sms-field">
                        <span className="sms-field-label">כותרת</span>
                        <input className="sms-input" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
                    </label>
                    <label className="sms-field">
                        <span className="sms-field-label">נוסח ההודעה</span>
                        <textarea
                            id="sms-edit-msg"
                            className="sms-textarea"
                            rows={4}
                            maxLength={480}
                            value={editing.message}
                            onChange={(e) => setEditing({ ...editing, message: e.target.value })}
                        />
                    </label>
                    <div className="sms-vars">
                        <span className="sms-vars-label">משתנים:</span>
                        {TEMPLATE_VARS.map((v) => (
                            <button key={v} type="button" className="sms-var-chip"
                                onClick={() => setEditing((cur) => ({ ...cur, message: `${cur.message || ''}${v}` }))}>
                                {v}
                            </button>
                        ))}
                    </div>
                    <label className="sms-toggle-row">
                        <span>תבנית פעילה</span>
                        <label className="sms-toggle">
                            <input type="checkbox" checked={editing.enabled} onChange={(e) => setEditing({ ...editing, enabled: e.target.checked })} />
                            <span className="sms-toggle-track"><span className="sms-toggle-thumb" /></span>
                        </label>
                    </label>
                </Modal>
            )}

            {/* ── Test-send modal ── */}
            {testOpen && <TestSendModal onClose={() => setTestOpen(false)} showFlash={showFlash} />}

            {/* ── Settings (sender name) modal ── */}
            {settingsOpen && (
                <SettingsModal
                    initialSender={senderName}
                    onClose={() => setSettingsOpen(false)}
                    onSave={async (name) => {
                        setSenderName(name);
                        setSettingsOpen(false);
                        await persist(templates, name, campaignText);
                    }}
                    saving={saving}
                />
            )}

            {/* ── Purchase CONTACT CTA (no billing act yet) ── */}
            {purchaseOpen && (
                <Modal
                    title="רכישת חבילת SMS"
                    onClose={() => setPurchaseOpen(false)}
                    footer={<button className="sms-btn sms-btn--brand" onClick={() => setPurchaseOpen(false)}>הבנתי</button>}
                >
                    <p className="sms-contact-text">
                        להגדלת מלאי ה-SMS צרו קשר עם צוות HOPpA ונשמח לסייע בבחירת החבילה המתאימה.
                    </p>
                    <a className="sms-contact-link" href="mailto:sales@hoppa.co.il?subject=רכישת חבילת SMS">
                        <Icon name="mail" size={16} /> sales@hoppa.co.il
                    </a>
                    <p className="sms-contact-note">* רכישה מקוונת תתווסף בקרוב.</p>
                </Modal>
            )}
        </div>
    );
}

// Test-send dialog — wired to the real sendTestMessage act.
function TestSendModal({ onClose, showFlash }) {
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('בדיקת מערכת HOPpA');
    const [sending, setSending] = useState(false);

    const send = async () => {
        const p = phone.trim();
        if (!/^0\d{8,9}$/.test(p)) { showFlash('err', 'מספר טלפון לא תקין'); return; }
        setSending(true);
        const res = await smsSvc.sendTest(p, message);
        setSending(false);
        if (res.ok) { showFlash('ok', `נשלח בהצלחה (${res.channel || 'sms'})`); onClose(); }
        else showFlash('err', res.note || 'השליחה נכשלה');
    };

    return (
        <Modal
            title="שליחת הודעת בדיקה"
            onClose={onClose}
            footer={(
                <>
                    <button className="sms-btn sms-btn--ghost" onClick={onClose}>ביטול</button>
                    <button className="sms-btn sms-btn--brand" onClick={send} disabled={sending}>
                        {sending ? 'שולח…' : 'שליחה'}
                    </button>
                </>
            )}
        >
            <label className="sms-field">
                <span className="sms-field-label">מספר טלפון</span>
                <input className="sms-input" inputMode="tel" placeholder="05XXXXXXXX" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </label>
            <label className="sms-field">
                <span className="sms-field-label">נוסח</span>
                <textarea className="sms-textarea" rows={3} maxLength={200} value={message} onChange={(e) => setMessage(e.target.value)} />
            </label>
            <p className="sms-contact-note">ההודעה תישלח עם קידומת "HOPPA בדיקה:".</p>
        </Modal>
    );
}

// Settings dialog — the sender id (persisted via saveSmsTemplates alongside templates).
function SettingsModal({ initialSender, onClose, onSave, saving }) {
    const [name, setName] = useState(initialSender || 'HOPPA');
    return (
        <Modal
            title="הגדרות SMS"
            onClose={onClose}
            footer={(
                <>
                    <button className="sms-btn sms-btn--ghost" onClick={onClose}>ביטול</button>
                    <button className="sms-btn sms-btn--brand" onClick={() => onSave(name.trim() || 'HOPPA')} disabled={saving}>שמירה</button>
                </>
            )}
        >
            <label className="sms-field">
                <span className="sms-field-label">שם השולח (Sender ID)</span>
                <input className="sms-input" maxLength={40} value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            <p className="sms-contact-note">מופיע כשם השולח בהודעות ה-SMS ללקוחות.</p>
        </Modal>
    );
}
